import React, { useState } from "react";
import { US_STATES } from "../lib/usStates";
import { T } from "../lib/theme";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 8;

/* ─── Access Gate ─────────────────────────────────────────
   Real per-user login (POST /api/login) with a signup toggle
   (POST /api/signup) and a forgot-password toggle (POST
   /api/reset-request). The old shared-password /api/gate is
   untouched and still reachable — this only changes what this
   screen presents and calls, not the backend auth paths that
   exist alongside it.
   Forgot mode never distinguishes whether the email matched a real
   account — reset-request.js returns the identical response either
   way, and this screen doesn't introduce a distinction the backend
   deliberately doesn't make. There's no email-sending here: the
   confirmation just points at kevin@cradlehq.app as plain, readable
   text (not only a mailto: link, which silently does nothing in a
   browser with no mail client configured) — the owner follows up and
   generates the actual reset link by hand via the admin dashboard.
   sessionStorage keeps someone logged in for that browser tab's
   session only — closing the tab requires re-entry, which is the
   right tradeoff for a private household app. */
export function GateScreen({onUnlock}){
  const[mode,setMode]=useState("login"); // "login" | "signup" | "forgot"
  const[email,setEmail]=useState("");
  const[password,setPassword]=useState("");
  const[confirmPassword,setConfirmPassword]=useState("");
  const[fullName,setFullName]=useState(""); // signup only, required
  const[phone,setPhone]=useState(""); // signup only, optional
  const[state,setState]=useState(""); // signup only, optional — used later for the EIA gas-price regional lookup, never required to create an account
  // signup only, REQUIRED as of this pass — market-listings.js falls back
  // to a hardcoded DFW zip when this is missing, silently showing a
  // household distant dealer listings as though they were local. State
  // stays optional below; it only ever softens a gas-price estimate, a
  // lower-stakes gap than a listings search anchored to the wrong city.
  const[zip,setZip]=useState("");
  const[err,setErr]=useState("");
  const[info,setInfo]=useState(""); // pending-approval / signup-success / reset-requested — visually distinct from err, not just different text
  const[busy,setBusy]=useState(false);
  const[requested,setRequested]=useState(false); // forgot mode: request has been sent, show the mailto convenience

  const switchMode=(next)=>{
    setMode(next);
    setPassword("");setConfirmPassword("");setFullName("");setPhone("");setState("");setZip("");
    setErr("");setInfo("");setRequested(false);
  };

  const validate=()=>{
    if(!email.trim()||!EMAIL_RE.test(email.trim()))return "Enter a valid email address.";
    if(mode==="forgot")return "";
    if(!password)return "Enter a password.";
    if(mode==="signup"){
      if(!fullName.trim())return "Enter your name.";
      if(!/^\d{5}$/.test(zip.trim()))return "Enter a 5-digit ZIP code.";
      if(password.length<MIN_PASSWORD_LENGTH)return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
      if(password!==confirmPassword)return "Passwords don't match.";
    }
    return "";
  };

  const submit=async(e)=>{
    e.preventDefault();
    setErr("");setInfo("");
    const validationError=validate();
    if(validationError){setErr(validationError);return;}

    setBusy(true);
    try{
      if(mode==="login"){
        const res=await fetch("/api/login",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:email.trim(),password})});
        const d=await res.json().catch(()=>({}));
        if(res.ok&&d.ok){
          // Same session cookie contract /api/gate already used — a
          // same-origin fetch, so the response's Set-Cookie is applied
          // automatically. No manual cookie handling needed here.
          sessionStorage.setItem("hf_auth","1");
          onUnlock();
        }else if(res.status===403){
          setInfo(d.error||"Your account is pending approval.");
        }else if(res.status>=500){
          setErr("Something went wrong — try again.");
        }else{
          // login.js deliberately returns the same message+status for a
          // nonexistent email and a wrong password — shown verbatim,
          // not re-split by field, so this screen doesn't undo that.
          setErr(d.error||"Invalid email or password.");
        }
      }else if(mode==="signup"){
        const res=await fetch("/api/signup",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:email.trim(),password,full_name:fullName.trim(),phone:phone.trim(),state,zip:zip.trim()})});
        const d=await res.json().catch(()=>({}));
        if(res.ok){
          // New signups land at access_level 'demo' now, not 'pending'
          // — immediately able to log in, which is what makes the
          // guided demo walkthrough reachable right after signup.
          // Chains straight into /api/login with the same credentials
          // rather than bouncing back to the login form and making the
          // user retype what they just entered — reuses login.js's own
          // session-minting logic rather than duplicating it here.
          const loginRes=await fetch("/api/login",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:email.trim(),password})});
          const loginD=await loginRes.json().catch(()=>({}));
          if(loginRes.ok&&loginD.ok){
            sessionStorage.setItem("hf_auth","1");
            onUnlock();
          }else{
            // Extremely unlikely (the account was just created with
            // these exact credentials) but not impossible — fall back
            // rather than leaving the user on a broken screen.
            setInfo("Account created — sign in below.");
            setMode("login");
            setPassword("");setConfirmPassword("");setFullName("");setPhone("");setState("");setZip("");
          }
        }else if(res.status>=500){
          setErr("Something went wrong — try again.");
        }else{
          setErr(d.error||"Could not create your account.");
        }
      }else{
        const res=await fetch("/api/reset-request",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:email.trim()})});
        const d=await res.json().catch(()=>({}));
        if(res.status>=500){
          setErr("Something went wrong — try again.");
        }else{
          // Same message shown regardless of whether the email
          // matched — reset-request.js is deliberately byte-identical
          // either way, and this screen must not introduce a
          // distinction the backend doesn't make.
          setInfo(d.message||"If that email has an account, a request has been logged and the owner will be in touch.");
          setRequested(true);
        }
      }
    }catch{
      setErr("Connection error — try again.");
    }
    setBusy(false);
  };

  const fieldStyle={width:"100%",boxSizing:"border-box",padding:"11px 12px",border:`1.5px solid ${T.border}`,borderRadius:8,fontSize:14,marginBottom:10,color:T.text,background:T.card};
  const linkBtn={background:"none",border:"none",color:T.teal,fontWeight:700,cursor:"pointer",fontSize:13,padding:0};

  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:T.bg,fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
      <form onSubmit={submit} noValidate style={{background:T.card,borderRadius:16,padding:32,maxWidth:340,width:"100%",boxShadow:T.shadow}}>
        <div style={{fontSize:20,fontWeight:800,marginBottom:4,color:T.text}}>HomeFleet</div>
        <div style={{fontSize:13,color:T.textSm,marginBottom:20}}>{mode==="login"?"Sign in to your household.":mode==="signup"?"Request access to HomeFleet.":"Reset your password."}</div>
        {mode==="signup"&&<input type="text" value={fullName} onChange={e=>setFullName(e.target.value)} autoFocus placeholder="Full name" autoComplete="name" style={fieldStyle}/>}
        {mode==="signup"&&<input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Phone (optional)" autoComplete="tel" style={fieldStyle}/>}
        {mode==="signup"&&<select value={state} onChange={e=>setState(e.target.value)} style={{...fieldStyle,color:state?T.text:T.textSm}}><option value="">State (optional)</option>{US_STATES.map(s=><option key={s.code} value={s.code}>{s.name}</option>)}</select>}
        {mode==="signup"&&<input type="text" inputMode="numeric" value={zip} onChange={e=>setZip(e.target.value.replace(/[^0-9]/g,"").slice(0,5))} placeholder="ZIP code" autoComplete="postal-code" style={fieldStyle}/>}
        {!(mode==="forgot"&&requested)&&<input type="email" value={email} onChange={e=>setEmail(e.target.value)} autoFocus={mode!=="signup"} placeholder="Email" autoComplete="email" style={fieldStyle}/>}
        {mode!=="forgot"&&<input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password" autoComplete={mode==="login"?"current-password":"new-password"} style={fieldStyle}/>}
        {mode==="signup"&&<input type="password" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} placeholder="Confirm password" autoComplete="new-password" style={fieldStyle}/>}
        {mode==="login"&&<div style={{textAlign:"right",marginBottom:10}}><button type="button" onClick={()=>switchMode("forgot")} disabled={busy} style={{...linkBtn,fontWeight:600,fontSize:12}}>Forgot password?</button></div>}
        {err&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{err}</div>}
        {info&&<div style={{fontSize:13,color:T.amber,background:T.amberL,border:`1px solid ${T.amberBorderStrong}`,borderRadius:8,padding:"9px 12px",marginBottom:10}}>{info}</div>}
        {mode==="forgot"&&requested&&<div style={{fontSize:12,color:T.textMuted,marginBottom:10}}>Or email <a href="mailto:kevin@cradlehq.app" style={{color:T.teal,fontWeight:600}}>kevin@cradlehq.app</a> directly.</div>}
        {!(mode==="forgot"&&requested)&&(
          <button type="submit" disabled={busy} style={{width:"100%",padding:"11px 0",background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:8,fontWeight:700,fontSize:14,opacity:busy?.6:1}}>
            {busy?(mode==="login"?"Signing in...":mode==="signup"?"Submitting...":"Sending..."):(mode==="login"?"Sign In":mode==="signup"?"Request Access":"Send Reset Request")}
          </button>
        )}
        <div style={{textAlign:"center",marginTop:14,fontSize:13,color:T.textMuted}}>
          {mode==="login"&&(
            <>Don't have an account? <button type="button" onClick={()=>switchMode("signup")} disabled={busy} style={linkBtn}>Sign up</button></>
          )}
          {mode==="signup"&&(
            <>Already have an account? <button type="button" onClick={()=>switchMode("login")} disabled={busy} style={linkBtn}>Sign in</button></>
          )}
          {mode==="forgot"&&(
            <>Remembered your password? <button type="button" onClick={()=>switchMode("login")} disabled={busy} style={linkBtn}>Sign in</button></>
          )}
        </div>
      </form>
    </div>
  );
}
